import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { AnimatedButton } from '../ui/AnimatedButton';
import { Send } from 'lucide-react';

interface ReadyInquiryDialogProps extends React.ComponentProps<typeof Button> {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  buttonType?: 'animated' | 'default';
  onConfirm: (comment?: string) => Promise<void> | void;
  children?: React.ReactNode;
  className?: string;
}

export function ReadyInquiryDialog({
  title,
  description,
  confirmText = 'generic.confirm',
  cancelText = 'generic.cancel',
  onConfirm,
  variant = 'default',
  buttonType = 'animated',
  disabled = false,
  children,
  className,
}: ReadyInquiryDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const trimmed = comment.trim();
      await onConfirm(trimmed ? trimmed : undefined);
      setComment('');
      setOpen(false);
    } catch {
      // Parent shows toast / handles errors
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {buttonType === 'animated' ? (
        <AnimatedButton icon={<Send className="h-4 w-4" />} variant={variant} disabled={disabled} className={cn(className)} title={title}>
          {children}
        </AnimatedButton>
        ) : (
          <Button variant={variant} disabled={disabled} className={cn(className)} title={title}>
            <Send className="h-4 w-4" />
            {children}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="ready-inquiry-comment">{t('inquiries.actions.ready.reviewerNoteLabel')}</Label>
          <Textarea
            id="ready-inquiry-comment"
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('inquiries.actions.ready.reviewerNotePlaceholder')}
            disabled={isLoading}
            maxLength={2000}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
            {t(cancelText)}
          </Button>
          <Button variant={variant} onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? t('generic.loading') : t(confirmText)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
